/**
 * Side-by-side photo viewer with bbox overlay for damage analysis.
 *
 * Renders a "PŘED → PO" pair for a single photo index. On the "PO" image
 * draws SVG rectangles for every damage that points to this photo via
 * `photo_index`. Hovering a card scrolls the corresponding photo into
 * view and pulses the box; clicking a box scrolls the matching card.
 *
 * The component is purely presentational — it doesn't fetch anything and
 * doesn't know about React Query. It receives the report + a callback to
 * say which damage was hovered/clicked.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { DamageItem, DamageReport } from '../api/damage-analysis'
import { displaySrcSet, displayUrl } from '../components/image-source'
import { Paper } from '../components/paper'

const SEVERITY_STROKE: Record<1 | 2 | 3, string> = {
	1: '#eab308', // yellow
	2: '#f97316', // orange
	3: '#ef4444', // red
}

type Props = {
	report: DamageReport
	hoveredDamageIdx: number | null
	activeDamageIdx: number | null
	onBoxClick?: (damageIdx: number) => void
	onBoxHover?: (damageIdx: number | null) => void
}

export function DamagePhotoViewer({
	report,
	hoveredDamageIdx,
	activeDamageIdx,
	onBoxClick,
	onBoxHover,
}: Props) {
	const afterPhotos = report.after_photos ?? []
	const beforePhotos = report.before_photos ?? []
	const damages = report.damages ?? []

	if (!afterPhotos.length) {
		return (
			<Paper className="p-4 text-sm text-slate-500">
				Foto-viewer není dostupný — report neobsahuje URL fotek (starší verze reportu nebo
				prázdná data).
			</Paper>
		)
	}

	// Group damages by photo_index (1-based). Index 0 = "unknown",
	// damages there get rendered without a host photo at the bottom.
	const damagesByPhoto = useMemo(() => {
		const map = new Map<number, Array<{ damage: DamageItem; index: number }>>()
		for (let i = 0; i < damages.length; i++) {
			const d = damages[i]
			const key = d.photo_index ?? 0
			const bucket = map.get(key)
			if (bucket) bucket.push({ damage: d, index: i })
			else map.set(key, [{ damage: d, index: i }])
		}
		return map
	}, [damages])

	const unassigned = damagesByPhoto.get(0) ?? []

	return (
		<div className="space-y-3">
			{afterPhotos.map((afterUrl, idx) => {
				const photoIndex = idx + 1
				const beforeUrl = beforePhotos[idx] ?? null
				const items = damagesByPhoto.get(photoIndex) ?? []
				return (
					<PhotoPair
						key={photoIndex}
						photoIndex={photoIndex}
						beforeUrl={beforeUrl}
						afterUrl={afterUrl}
						items={items}
						hoveredDamageIdx={hoveredDamageIdx}
						activeDamageIdx={activeDamageIdx}
						onBoxClick={onBoxClick}
						onBoxHover={onBoxHover}
					/>
				)
			})}
			{unassigned.length > 0 ? (
				<Paper className="p-3 text-xs text-slate-500">
					<strong>Bez přiřazené fotky:</strong> {unassigned.length} poškození, kde model
					neurčil photo_index.
				</Paper>
			) : null}
		</div>
	)
}

function PhotoPair({
	photoIndex,
	beforeUrl,
	afterUrl,
	items,
	hoveredDamageIdx,
	activeDamageIdx,
	onBoxClick,
	onBoxHover,
}: {
	photoIndex: number
	beforeUrl: string | null
	afterUrl: string
	items: Array<{ damage: DamageItem; index: number }>
	hoveredDamageIdx: number | null
	activeDamageIdx: number | null
	onBoxClick?: (damageIdx: number) => void
	onBoxHover?: (damageIdx: number | null) => void
}) {
	const wrapperRef = useRef<HTMLDivElement>(null)
	const isActiveHere = items.some(it => it.index === activeDamageIdx)

	// Auto-scroll into view when the corresponding damage card is activated.
	useEffect(() => {
		if (isActiveHere && wrapperRef.current) {
			wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
		}
	}, [isActiveHere])

	const afterSrc = { url: displayUrl(afterUrl), srcSet: displaySrcSet(afterUrl) }
	const beforeSrc = beforeUrl
		? { url: displayUrl(beforeUrl), srcSet: displaySrcSet(beforeUrl) }
		: null

	return (
		<div ref={wrapperRef} className="bg-white rounded-xl border border-slate-200 p-3">
			<div className="mb-2 flex items-center justify-between text-xs text-slate-500">
				<span>
					Úhel {photoIndex}
					{items.length > 0 ? (
						<span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">
							{items.length} {items.length === 1 ? 'poškození' : 'poškození'}
						</span>
					) : (
						<span className="ml-2 text-emerald-600">✓ bez poškození</span>
					)}
				</span>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
				<div>
					<div className="text-xxs font-semibold uppercase tracking-wide text-slate-400 mb-1">
						PŘED
					</div>
					{beforeSrc ? (
						<img
							src={beforeSrc.url}
							srcSet={beforeSrc.srcSet}
							sizes="(max-width: 768px) 100vw, 50vw"
							alt={`Před #${photoIndex}`}
							className="w-full rounded-lg bg-slate-100 object-cover aspect-[4/3]"
							loading="lazy"
						/>
					) : (
						<div className="aspect-[4/3] rounded-lg bg-slate-100 flex items-center justify-center text-xs text-slate-400">
							(žádná předchozí fotka)
						</div>
					)}
				</div>
				<div>
					<div className="text-xxs font-semibold uppercase tracking-wide text-slate-400 mb-1">
						PO
					</div>
					<AfterImageWithOverlay
						src={afterSrc?.url ?? afterUrl}
						srcSet={afterSrc?.srcSet}
						alt={`Po #${photoIndex}`}
						items={items}
						hoveredDamageIdx={hoveredDamageIdx}
						activeDamageIdx={activeDamageIdx}
						onBoxClick={onBoxClick}
						onBoxHover={onBoxHover}
					/>
				</div>
			</div>
		</div>
	)
}

function AfterImageWithOverlay({
	src,
	srcSet,
	alt,
	items,
	hoveredDamageIdx,
	activeDamageIdx,
	onBoxClick,
	onBoxHover,
}: {
	src: string
	srcSet?: string
	alt: string
	items: Array<{ damage: DamageItem; index: number }>
	hoveredDamageIdx: number | null
	activeDamageIdx: number | null
	onBoxClick?: (damageIdx: number) => void
	onBoxHover?: (damageIdx: number | null) => void
}) {
	const [loaded, setLoaded] = useState(false)
	return (
		<div className="relative">
			<img
				src={src}
				srcSet={srcSet}
				sizes="(max-width: 768px) 100vw, 50vw"
				alt={alt}
				className={`w-full rounded-lg bg-slate-100 object-cover aspect-[4/3] transition-opacity ${
					loaded ? 'opacity-100' : 'opacity-0'
				}`}
				loading="lazy"
				onLoad={() => setLoaded(true)}
			/>
			{!loaded ? (
				<div className="absolute inset-0 flex items-center justify-center rounded-lg bg-slate-100">
					<div className="w-6 h-6 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
				</div>
			) : null}
			{loaded ? (
				<svg
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
					className="absolute inset-0 w-full h-full pointer-events-none"
				>
					{items.map(({ damage, index }) => {
						if (!damage.bbox) return null
						const sev = damage.severity
						const stroke = SEVERITY_STROKE[sev]
						const isHovered = index === hoveredDamageIdx
						const isActive = index === activeDamageIdx
						const opacity = isHovered || isActive ? 1 : 0.78
						const strokeWidth = isHovered || isActive ? 0.9 : 0.55
						return (
							<g
								key={index}
								className="pointer-events-auto cursor-pointer"
								onClick={() => onBoxClick?.(index)}
								onMouseEnter={() => onBoxHover?.(index)}
								onMouseLeave={() => onBoxHover?.(null)}
							>
								<rect
									x={damage.bbox.x * 100}
									y={damage.bbox.y * 100}
									width={damage.bbox.w * 100}
									height={damage.bbox.h * 100}
									fill="transparent"
									stroke={stroke}
									strokeWidth={strokeWidth}
									opacity={opacity}
									vectorEffect="non-scaling-stroke"
								/>
								<rect
									x={damage.bbox.x * 100}
									y={Math.max(0, damage.bbox.y * 100 - 2.5)}
									width={Math.max(damage.bbox.w * 100, 4)}
									height={2.4}
									fill={stroke}
									opacity={opacity}
								/>
								<text
									x={damage.bbox.x * 100 + 0.4}
									y={Math.max(1.6, damage.bbox.y * 100 - 0.6)}
									fontSize="1.6"
									fill="white"
									fontWeight="700"
									className="select-none"
								>
									{index + 1}
								</text>
							</g>
						)
					})}
				</svg>
			) : null}
		</div>
	)
}
